import React from 'react'
import _ from 'lodash'
import moment from 'momentExt'
import queryString from 'query-string'
import { setCookie, getCookie } from 'cookie'
import { accountLinkingUrls } from 'urls'
import numeral from 'numeral'
import axios from 'axios'
import store from '../app/store'
import { formatters } from 'modules/formatting'
import { dateTypes, SLACK_CLIENT_ID, SLACK_REDIRECT_URI, SLACK_SCOPES } from 'modules/constants'
import { isShopifyEmbedded, getShopifySessionToken } from 'modules/shopifySessionToken'
import { formatNameValueDisplay } from 'modules/formatters'
import 'modules/websiteAttribution'

/**
 * Global axios interceptor for Shopify session token authentication
 * Automatically adds Authorization header with session token for ALL axios requests
 * when the app is running inside Shopify Admin iframe
 * This enables "Built for Shopify" certification compliance
 *
 * PERFORMANCE: Only adds token if App Bridge is already available (no polling/waiting)
 * This prevents 1s delay on first API call during login
 */
axios.interceptors.request.use(
  async (config) => {
    // Only add session token when running inside Shopify AND App Bridge is ready
    const isEmbedded = isShopifyEmbedded()
    // Check if App Bridge is ALREADY available (no waiting/polling)
    const hasAppBridge = typeof window !== 'undefined' && !!window.shopify?.idToken

    if (isEmbedded && hasAppBridge) {
      try {
        const sessionToken = await getShopifySessionToken()
        if (sessionToken && config.headers) {
          config.headers['Authorization'] = `Bearer ${sessionToken}`
        }
      } catch (error) {
        // Don't block the request - just proceed without the token
        console.debug('[Shopify] Could not add session token to request:', error)
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

export {
  saveAsCSV,
  saveAsGoogleDrive,
  downloadCsvBlobParts,
  buildCsvPartsForDataset,
} from './csvExport'

export const updateUrlWithView = (viewIndex, viewList, appName) => {
  const search = queryString.stringify({
    ...queryString.parse(location.search),
    view: viewList[viewIndex],
  })
  history.replaceState('', '', `/${appName}?${search}`)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
}

export const updateUrlQuery = (newQueryJson, replaceState) => {
  const currentQueryJson = queryString.parse(location.search)
  let queryJson = {
    ...currentQueryJson,
    ...newQueryJson,
  }
  if (newQueryJson.dateFilters) {
    delete queryJson.dateFilters
    for (const type of Object.keys(newQueryJson.dateFilters.dateTypes)) {
      if (newQueryJson.dateFilters.dateTypes[type].active) {
        queryJson[
          type
        ] = `${newQueryJson.dateFilters.dateTypes[type].dateRange.start}to${newQueryJson.dateFilters.dateTypes[type].dateRange.end}`
        if (newQueryJson.dateFilters.dateTypes[type].compareDateRange) {
          queryJson[
            `compare-${type}`
          ] = `${newQueryJson.dateFilters.dateTypes[type].compareDateRange.start}to${newQueryJson.dateFilters.dateTypes[type].compareDateRange.end}`
        }
      } else {
        delete queryJson[type]
      }
    }
  }
  if (newQueryJson.rangeFilters) {
    delete queryJson.rangeFilters
    for (const type of Object.keys(newQueryJson.rangeFilters.rangeTypes)) {
      if (newQueryJson.rangeFilters.rangeTypes[type].active) {
        queryJson[
          type
        ] = `${newQueryJson.rangeFilters.rangeTypes[type].min}to${newQueryJson.rangeFilters.rangeTypes[type].max}`
      } else {
        delete queryJson[type]
      }
    }
  }
  if (newQueryJson.keywordFilters) {
    delete queryJson.keywordFilters
    for (const type of Object.keys(newQueryJson.keywordFilters.keywordTypes)) {
      if (newQueryJson.keywordFilters.keywordTypes[type].active) {
        queryJson[type] = `${newQueryJson.keywordFilters.keywordTypes[type].containing ? 'is' : 'not'}-${
          newQueryJson.keywordFilters.keywordTypes[type].value
        }`
      } else {
        delete queryJson[type]
      }
    }
  }
  Object.keys(queryJson).forEach((field) => {
    if (!queryJson[field]) {
      delete queryJson[field]
    }
  })
  if (queryJson.startDate && queryJson.endDate) {
    const { startDate, endDate, periodCompare, ...restQueries } = queryJson
    queryJson = {
      ...restQueries,
      startDate,
      endDate,
    }
    if (periodCompare) {
      queryJson = {
        ...queryJson,
        periodCompare,
      }
    }
  }
  if (queryJson.timePicker?.startTime && queryJson.timePicker?.endTime) {
    const { startTime, endTime } = queryJson.timePicker
    const { ...restQueries } = queryJson
    queryJson = {
      ...restQueries,
      startTime,
      endTime,
    }
  }
  const search = queryString.stringify(queryJson, { sort: false })
  const allQueryKeys = [...new Set([...Object.keys(currentQueryJson), ...Object.keys(queryJson)])]
  if (!location.search && !search) return
  if (
    allQueryKeys.length === Object.keys(queryJson).length && // identical query keys
    allQueryKeys.reduce((acc, cur) => acc && currentQueryJson[cur] === queryJson[cur], true) // identical query values
  ) {
    return
  }
  if (location.search === '?' + search) return
  history[replaceState ? 'replaceState' : 'pushState']('', '', `${location.pathname}${search ? '?' + search : ''}`)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
}

export const topRedirect = ({
  url,
  waitingTime = 1000 * 60 * 20, // optional
  referer = store.getState().root.topUrl || location.href, // optional
  newWindow = false,
  successMessage = '',
  ignoreSuccessMessage = false,
}) => {
  setCookie('auth_referer', referer, waitingTime)
  if (newWindow) {
    setCookie('auth_referer', location.href, waitingTime)
    setCookie('waiting_for_close', 'true', 1000 * 60 * 5)
    if (successMessage) {
      setCookie('pending_success_message', successMessage, 1000 * 60 * 5)
    }
    if (ignoreSuccessMessage) {
      setCookie('ignore_success_message', 'true', 1000 * 60 * 5)
    }
    const nw = window.open(url)
    if (!nw) {
      // Popup blocked. Navigating this frame would fail for providers that refuse
      // to be framed, so break out to the top window instead of dropping the click.
      setCookie('waiting_for_close', '')
      setCookie('auth_referer', referer, waitingTime)
      try {
        window.top.location.href = url
      } catch (e) {
        window.location.href = url
      }
      return
    }
    const checkClose = setInterval(() => {
      if (nw.closed) {
        location.href = location.href // refresh the current page
      }
    }, 1000)
    setTimeout(() => {
      setCookie('waiting_for_close', '')
      clearInterval(checkClose)
    }, 1000 * 60 * 5)
  }
  else window.location.href = url
}

export const topRedirectToShopifyAppStore = () => {
  topRedirect({ url: accountLinkingUrls.shopifyAppStore })
}

export const topRedirectToBigcommerceAppStore = () => {
  topRedirect({ url: accountLinkingUrls.bigcommerceAppStore })
}

export const topRedirectToEtsy = () => {
  topRedirect({ url: accountLinkingUrls.etsy })
}

export const topRedirectToHubspot = () => {
  topRedirect({ url: accountLinkingUrls.hubspot })
}

export const topRedirectToUPS = () => {
  topRedirect({ url: accountLinkingUrls.ups })
}

export const topRedirectToEbay = () => {
  let newWindow = window !== window.top
  topRedirect({ url: accountLinkingUrls.ebay, newWindow: newWindow })
}

export const topRedirectToWix = () => {
  topRedirect({ url: accountLinkingUrls.wixAppStore })
}

export const topRedirectToWalmart = () => {
  let newWindow = window !== window.top
  topRedirect({ url: accountLinkingUrls.walmart, newWindow: newWindow })
}

export const topRedirectToSquare = () => {
  let newWindow = window !== window.top
  topRedirect({ url: accountLinkingUrls.square, newWindow: newWindow })
}

export const buildSlackAuthorizeUrl = () => {
  const unique_user_id = getCookie('unique_user_id') // app_token doubles as the user identifier in OAuth `state`
  return `https://slack.com/oauth/v2/authorize?redirect_uri=${SLACK_REDIRECT_URI}&client_id=${SLACK_CLIENT_ID}&state=${unique_user_id}&scope=${SLACK_SCOPES}`
}

// slack.com/oauth/v2/authorize refuses to be framed, so a same-tab redirect dies
// when PayHelm runs embedded (WordPress wp-admin, Shopify admin). Pop out there.
export const topRedirectToSlack = () => {
  topRedirect({ url: buildSlackAuthorizeUrl(), newWindow: window !== window.top })
}

export const convertToDateRange = (datePickerStore) => {
  if (!datePickerStore.startDate || !datePickerStore.endDate) {
    return null
  }
  return moment.range(datePickerStore.startDate, datePickerStore.endDate)
}

export const getDateRangesToCompare = ({ startDate, endDate, periodCompare, periodNum = 1 }) => {
  if (!periodCompare || periodCompare === 'none' || periodCompare === 'custom_period') {
    return []
  }
  let dateRanges = [],
    start = moment(startDate),
    end = moment(endDate),
    unit = 'days',
    number = 1
  if (periodCompare === 'previous_year') {
    for (let i = 0; i < periodNum; i++) {
      dateRanges.push({
        startDate: moment(startDate).subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: moment(endDate).subtract(1, 'year').format('YYYY-MM-DD'),
      })
    }
  } else {
    if (startDate === endDate) {
      let cursor = moment(startDate)
      for (let j = 0; j < periodNum; j++) {
        cursor = cursor.clone().subtract(7, 'days')
        const d = cursor.format('YYYY-MM-DD')
        dateRanges.push({
          startDate: d,
          endDate: d,
        })
      }
    } else if (start.isSame(moment(endDate).subtract(1, 'year').add(1, 'day'))) {
      unit = 'year'
      number = 1
      for (let i = 0; i < periodNum; i++) {
        end = moment(start).subtract(1, 'day')
        start = moment(end).subtract(number, unit).add(1, 'day')
        dateRanges.push({
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
        })
      }
    } else if (start.isSame(moment(endDate).subtract(1, 'month').add(1, 'day'))) {
      unit = 'month'
      number = 1
      for (let k = 0; k < periodNum; k++) {
        end = moment(start).subtract(1, 'day')
        start = moment(end).subtract(number, unit).add(1, 'day')
        dateRanges.push({
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
        })
      }
    } else {
      number = moment.duration(end.diff(start)).asDays() + 1
      for (let m = 0; m < periodNum; m++) {
        end = moment(start).subtract(1, 'day')
        start = moment(end).subtract(number, unit).add(1, 'day')
        dateRanges.push({
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
        })
      }
    }
  }
  return dateRanges
}

/** Short label for dashboard compare pill / date filter (matches primary date pill style). */
export const formatDashboardCompareRange = (startDate, endDate) => {
  if (!startDate || !endDate) return ''
  const startM = moment(startDate)
  const endM = moment(endDate)
  if (!startM.isValid() || !endM.isValid()) return ''
  const sameYear = startM.year() === endM.year()
  const sameDay = startM.isSame(endM, 'day')
  if (sameDay) return startM.format('MMM D, YYYY')
  const fromLabel = sameYear ? startM.format('MMM D') : startM.format('MMM D YYYY')
  return `${fromLabel} – ${endM.format('MMM D, YYYY')}`
}

/**
 * Resolved compare window for the active date filter (dashboard filter bar + Solr compare requests).
 * @param { { dateTypes: Record<string, { active?: boolean; dateRange?: { start?: string; end?: string }; compareDateRange?: { start?: string; end?: string } }> } } dateFiltersState — slice `dateFilters` from Redux
 */
export const getResolvedComparePeriod = (dateFiltersState, periodCompare) => {
  if (!periodCompare || periodCompare === 'none' || !dateFiltersState?.dateTypes) return null
  const dateTypes = dateFiltersState.dateTypes
  const activeType = Object.keys(dateTypes).find((t) => dateTypes[t].active)
  if (!activeType) return null
  const t = dateTypes[activeType]
  const primaryStart = t.dateRange?.start
  const primaryEnd = t.dateRange?.end
  if (!primaryStart || !primaryEnd) return null

  let cmpStart
  let cmpEnd
  let modeLabel
  if (periodCompare === 'custom_period') {
    cmpStart = t.compareDateRange?.start
    cmpEnd = t.compareDateRange?.end
    modeLabel = 'custom'
    if (!cmpStart || !cmpEnd) {
      return {
        startDate: null,
        endDate: null,
        modeLabel,
        formatCompareRange: 'Select dates',
        formatPrimaryRange: formatDashboardCompareRange(primaryStart, primaryEnd),
        tooltip: 'Select a custom compare range in the date picker',
      }
    }
  } else if (periodCompare === 'previous_year') {
    const [r] = getDateRangesToCompare({ startDate: primaryStart, endDate: primaryEnd, periodCompare: 'previous_year' })
    if (!r) return null
    cmpStart = r.startDate
    cmpEnd = r.endDate
    modeLabel = 'previous year'
  } else {
    const [r] = getDateRangesToCompare({ startDate: primaryStart, endDate: primaryEnd, periodCompare: 'previous' })
    if (!r) return null
    cmpStart = r.startDate
    cmpEnd = r.endDate
    modeLabel = 'previous period'
  }

  const formatCompareRange = formatDashboardCompareRange(cmpStart, cmpEnd)
  const formatPrimaryRange = formatDashboardCompareRange(primaryStart, primaryEnd)
  return {
    startDate: cmpStart,
    endDate: cmpEnd,
    modeLabel,
    formatCompareRange,
    formatPrimaryRange,
    tooltip: `Comparing ${formatPrimaryRange} against ${formatCompareRange}`,
  }
}

export const getQuickRanges = () => {
  const dayToday = moment().day()
  return {
    Today: moment.range(moment(), moment()),
    Yesterday: moment.range(moment().subtract(1, 'day'), moment().subtract(1, 'day')),
    divider1: {
      divider: true,
      className: 'divider-col-2',
    },
    'Last Week': moment.range(moment().subtract(7 + dayToday, 'days'), moment().subtract(1 + dayToday, 'days')),
    divider2: {
      divider: true,
      className: 'divider-col-3',
    },
    'This Week': moment.range(moment().subtract(dayToday, 'days'), moment()),
    divider3: {
      divider: true,
      className: 'divider-col-2',
    },
    'Last 7 Days': moment.range(moment().subtract(6, 'days'), moment()),
    'Last 14 Days': moment.range(moment().subtract(13, 'days'), moment()),
    divider4: {
      divider: true,
      className: 'divider-col-2',
    },
    divider41: {
      divider: true,
      className: 'divider-col-3',
    },
    'Last Month': moment.range(moment().subtract(1, 'month').date(1), moment().date(1).subtract(1, 'day')),
    'This Month': moment.range(moment().date(1), moment()),
    divider5: {
      divider: true,
      className: 'divider-col-2',
    },
    'Last 30 Days': moment.range(moment().subtract(29, 'days'), moment()),
    divider6: {
      divider: true,
      className: 'divider-col-3',
    },
    'Last 90 Days': moment.range(moment().subtract(89, 'days'), moment()),
    divider7: {
      divider: true,
      className: 'divider-col-2',
    },
    'This Quarter': moment.range(moment().quarter(moment().quarter()).startOf('quarter'), moment()),
    'Last Quarter': moment.range(
      moment()
        .quarter(moment().quarter() - 1)
        .startOf('quarter'),
      moment()
        .quarter(moment().quarter() - 1)
        .endOf('quarter')
    ),
    divider8: {
      divider: true,
      className: 'divider-col-2',
    },
    divider81: {
      divider: true,
      className: 'divider-col-3',
    },
    'Last Year': moment.range(
      moment().subtract(1, 'year').month(0).date(1),
      moment().month(0).date(1).subtract(1, 'day')
    ),
    'Last 365 Days': moment.range(moment().subtract(364, 'day'), moment()),
    divider9: {
      divider: true,
      className: 'divider-col-2',
    },
    'This Year': moment.range(moment().month(0).date(1), moment()),
    divider10: {
      divider: true,
      className: 'divider-col-3',
    },
    'Last 730 Days': moment.range(moment().subtract(729, 'day'), moment()),
  }
}

export const reportDateRange = [
  'Today',
  'Yesterday',
  'Last Week',
  'This Week',
  'Last 7 Days',
  'Last 14 Days',
  'Last Month',
  'This Month',
  'Last 30 Days',
  'Last 90 Days',
  'This Quarter',
  'Last Quarter',
  'Last Year',
  'Last 365 Days',
  'This Year',
]

export const switchToUrlView = (nextView, viewList, viewIndex, setView) => {
  if (nextView) {
    const nextViewIndex = viewList.indexOf(nextView)
    if (nextViewIndex >= 0 && nextViewIndex !== viewIndex) {
      setView(nextViewIndex)
    }
  }
}

export const getQueryJson = () => {
  const query = queryString.parse(location.search)
  const min_price = parseFloat(query.min_price) || null
  const max_price = parseFloat(query.max_price) || null
  return { ...query, min_price, max_price }
}

// Drap & drop utils
const dragOverEntry = (e, index, component) => {
  e.preventDefault()
  component.setState({ draggedOverEntryIndex: index })
}

const dragEntry = (index, component) => {
  component.setState({ draggedEntryIndex: index })
}

const dragEnd = (component) => {
  component.setState({ draggedEntryIndex: null, draggedOverEntryIndex: null })
}

const dropEntry = (index, component, entriesToSort, callback) => {
  const { draggedEntryIndex } = component.state,
    droppedEntryIndex = index
  if (draggedEntryIndex === droppedEntryIndex) {
    component.setState({ draggedEntryIndex: null, draggedOverEntryIndex: null })
    return
  }
  const nextEntries =
    draggedEntryIndex < droppedEntryIndex
      ? [
          // drop after
          ...entriesToSort.slice(0, draggedEntryIndex),
          ...entriesToSort.slice(draggedEntryIndex + 1, droppedEntryIndex + 1),
          entriesToSort[draggedEntryIndex],
          ...entriesToSort.slice(droppedEntryIndex + 1),
        ]
      : [
          // drop before
          ...entriesToSort.slice(0, droppedEntryIndex),
          entriesToSort[draggedEntryIndex],
          ...entriesToSort.slice(droppedEntryIndex, draggedEntryIndex),
          ...entriesToSort.slice(draggedEntryIndex + 1),
        ]
  component.setState({ draggedEntryIndex: null, draggedOverEntryIndex: null })
  callback(nextEntries)
}

export const dragAndDrop = {
  dragOverEntry,
  dragEntry,
  dragEnd,
  dropEntry,
}

/**
 * Get the default axios configuration with common headers
 * This includes session signature, path info, and referrer hostname
 */
export const getAxiosDefaultConfig = () => ({
  withCredentials: true,
  headers: {
    ['session-signature']: store.getState().root.sessionSignature,
    path: location.pathname,
    search: location.search,
    ...(
      (document.referrer && (window !== window.top)) ? {
        'referrer-hostname': (new URL(document.referrer)).hostname
      } : null
    )
  },
})

export const getInitialTrendKeys = () => {
  const trendKeys = [
    localStoragePayhelm.dashboard_overview_primary_metric_key,
    localStoragePayhelm.dashboard_overview_secondary_metric_key,
  ]
  const trendTitles = [
    localStoragePayhelm.dashboard_overview_primary_metric,
    localStoragePayhelm.dashboard_overview_secondary_metric,
  ]

  if (trendKeys[0]) return trendKeys
  if (!trendTitles[0] && !trendTitles[1]) return ['items_sold', 'null']
  if (!trendTitles[0]) trendTitles[0] = 'Items Sold'
  return trendTitles.map((title) => {
    const trendKey = _.findKey(formatters.trends, (trend) => trend.title === title)
    return trendKey ? formatters.trends[trendKey].csvKey : 'null'
  })
}

/**
 * Strip `null`, `undefined`, and whitespace-only entries from a filter array.
 * If that empties the array, restore the dashboard reducer's `[null]` sentinel
 * so downstream consumers (e.g. `getEffectiveFilterKeys`) keep treating it as
 * "no selection" rather than as an empty selection that matches nothing.
 */
const cleanFilterArray = (arr: any): any[] => {
  if (!Array.isArray(arr)) return [null]
  const cleaned = arr.filter((v) => v != null && String(v).trim() !== '')
  return cleaned.length ? cleaned : [null]
}

/**
 * Defensively normalize a redux filters snapshot before constructing a search
 * query. Handles the cold-load race where a `useCallback`-captured snapshot can
 * still hold the `[null]` sentinel (or a partially-populated `[null, 'Shipped']`
 * during a re-render) by stripping nulls/blanks from each filter array. Pure
 * `[null]` arrays remain `[null]` (still "no selection"), so existing behavior
 * is preserved when a filter is genuinely unset.
 *
 * Apply at every callsite that builds a Solr search body from redux filters
 * (Dashboard, Shipping, FilterEditor, ExportExtensiveButton, Reports, etc.) so
 * deep-linked report pages don't fire unscoped queries on hard refresh.
 */
export const sanitizeFilters = (filters: any) => {
  if (!filters || typeof filters !== 'object') return filters
  const out: any = {}
  for (const key of Object.keys(filters)) {
    const v = filters[key]
    if (Array.isArray(v)) {
      out[key] = cleanFilterArray(v)
    } else if (v && typeof v === 'object') {
      const nested: any = {}
      for (const k of Object.keys(v)) {
        nested[k] = cleanFilterArray((v as any)[k])
      }
      out[key] = nested
    } else {
      out[key] = v
    }
  }
  return out
}

/**
 * Coerce a filter entry to an array. Legacy custom reports (saved before the
 * order/product metafield map migration in 06416a39e) persist metafield facets
 * as `[null]` arrays, which the dashboard reducer can spread into map-shaped
 * `{"0": null}` state. Callers deref entries as arrays (`.includes(null)`), so
 * a non-array entry must be normalized to `[null]` ("no selection") to avoid
 * `null.includes` TypeErrors that tear down the render tree.
 * @param {*} v filter entry value
 */
export const asFilterArray = (v) => (Array.isArray(v) ? v : [null])

/**
 * Drop array-shaped (legacy) order/product metafield filters to `{}` so they
 * aren't merged into map-shaped redux state as garbage. Metafield facets must
 * be name→value maps; a saved report holding them as arrays predates the
 * migration and carries no real selection.
 * @param {Object} filters filters object (mutated copy returned)
 */
export const normalizeMetafieldFilters = (filters) => {
  if (!filters || typeof filters !== 'object') return filters
  const next = { ...filters }
  for (const key of ['order_metafields', 'product_metafields', '-order_metafields', '-product_metafields']) {
    const val = next[key]
    if (val !== undefined && (Array.isArray(val) || typeof val !== 'object' || val === null)) {
      delete next[key]
    }
  }
  return next
}

/**
 * Get effective general & option filter keys from the redux filters
 * @param {Object} filters filters object in redux store
 */
export const getEffectiveFilterKeys = (filters) => {
  const {
    options: optionFilters = {},
    custom_fields: customFieldFilters = {},
    customer_form_fields: customerFormFieldFilters = {},
    shipping_form_fields: shippingFormFieldFilters = {},
    customer_custom_attributes: customerCustomAttributesFilters = {},
    order_metafields: orderMetafieldFilters = {},
    product_metafields: productMetafieldFilters = {},
    ...generalFilters
  } = filters

  return {
    effectiveFilterKeys: Object.keys(generalFilters).filter((val) => !generalFilters[val].includes(null)),
    effectiveOptionFilterKeys: Object.keys(optionFilters).filter((val) => !optionFilters[val].includes(null)),
    effectiveCustomFieldFilterKeys: Object.keys(customFieldFilters).filter(
      (val) => !customFieldFilters[val].includes(null)
    ),
    effectiveCustomerFormFieldFilterKeys: Object.keys(customerFormFieldFilters).filter(
      (val) => !customerFormFieldFilters[val].includes(null)
    ),
    effectiveShippingFormFieldFilterKeys: Object.keys(shippingFormFieldFilters).filter(
      (val) => !shippingFormFieldFilters[val].includes(null)
    ),
    effectiveCustomerCustomAttributesFilterKeys: Object.keys(customerCustomAttributesFilters).filter(
      (val) => !customerCustomAttributesFilters[val].includes(null)
    ),
    effectiveOrderMetafieldFilterKeys: Object.keys(orderMetafieldFilters).filter(
      (val) => !asFilterArray(orderMetafieldFilters[val]).includes(null)
    ),
    effectiveProductMetafieldFilterKeys: Object.keys(productMetafieldFilters).filter(
      (val) => !asFilterArray(productMetafieldFilters[val]).includes(null)
    ),
  }
}

/**
 * Insert or update query in URL
 * @param {String} searchTerms search keywords
 * @param {Object} filters raw filters
 * @param {Array} effectiveFilterKeys keys of filters that don't include "All *s"
 */
export const getNewQueryJson = (
  searchTerms,
  searchExcludeTerms,
  dateType,
  filters,
  {
    effectiveFilterKeys,
    effectiveOptionFilterKeys,
    effectiveCustomFieldFilterKeys,
    effectiveCustomerFormFieldFilterKeys,
    effectiveShippingFormFieldFilterKeys,
    effectiveCustomerCustomAttributesFilterKeys,
    effectiveOrderMetafieldFilterKeys = [],
    effectiveProductMetafieldFilterKeys = [],
  },
  filterLogicOperator = 'AND'
) => {
  const newQueryJson = {
    dateType,
    join: filterLogicOperator,
    q:
      !searchTerms &&
      !searchExcludeTerms &&
      !effectiveFilterKeys.length &&
      !effectiveOptionFilterKeys.length &&
      !effectiveCustomFieldFilterKeys.length &&
      !effectiveCustomerFormFieldFilterKeys.length &&
      !effectiveShippingFormFieldFilterKeys.length &&
      !effectiveCustomerCustomAttributesFilterKeys.length &&
      !effectiveOrderMetafieldFilterKeys.length &&
      !effectiveProductMetafieldFilterKeys.length
        ? null
        : JSON.stringify({
            ...(searchTerms
              ? {
                  title: searchTerms,
                }
              : {}),
            ...(searchExcludeTerms
              ? {
                  title_ex: searchExcludeTerms,
                }
              : {}),
            ...(effectiveFilterKeys.length
              ? {
                  filters: Object.assign(
                    {},
                    ...effectiveFilterKeys.map((key) => {
                      return { [key]: filters[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveOptionFilterKeys.length
              ? {
                  option_filters: Object.assign(
                    {},
                    ...effectiveOptionFilterKeys.map((key) => {
                      return { [key]: filters.options[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveCustomFieldFilterKeys.length
              ? {
                  custom_field_filters: Object.assign(
                    {},
                    ...effectiveCustomFieldFilterKeys.map((key) => {
                      return { [key]: filters.custom_fields[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveCustomerFormFieldFilterKeys.length
              ? {
                  customer_form_field_filters: Object.assign(
                    {},
                    ...effectiveCustomerFormFieldFilterKeys.map((key) => {
                      return { [key]: filters.customer_form_fields[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveShippingFormFieldFilterKeys.length
              ? {
                  shipping_form_field_filters: Object.assign(
                    {},
                    ...effectiveShippingFormFieldFilterKeys.map((key) => {
                      return { [key]: filters.shipping_form_fields[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveCustomerCustomAttributesFilterKeys.length
              ? {
                  customer_attributes_filters: Object.assign(
                    {},
                    ...effectiveCustomerCustomAttributesFilterKeys.map((key) => {
                      return { [key]: filters.customer_custom_attributes[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveOrderMetafieldFilterKeys.length
              ? {
                  order_metafield_filters: Object.assign(
                    {},
                    ...effectiveOrderMetafieldFilterKeys.map((key) => {
                      return { [key]: filters.order_metafields[key] }
                    })
                  ),
                }
              : {}),
            ...(effectiveProductMetafieldFilterKeys.length
              ? {
                  product_metafield_filters: Object.assign(
                    {},
                    ...effectiveProductMetafieldFilterKeys.map((key) => {
                      return { [key]: filters.product_metafields[key] }
                    })
                  ),
                }
              : {}),
          }),
  }
  return newQueryJson
}

export const getDefaultFilterType = (filters) => {
  let defaultFilterType,
    activeFilters = 0,
    ultimateFilter
  for (const type of Object.keys(filters)) {
    if (filters[type]?.active) {
      activeFilters = activeFilters + 1
      ultimateFilter = type
      if (!defaultFilterType) defaultFilterType = type
    }
  }
  if (!defaultFilterType) defaultFilterType = Object.keys(filters)[0]
  return { defaultFilterType, activeFilters, ultimateFilter }
}

const getLocalStorage = () => {
  try {
    const storage = window.localStorage
    return storage
  } catch (error) {
    console.error('Browser window local storage is not accessible')
    return {}
  }
}

export const localStoragePayhelm = getLocalStorage()

const getSessionStorage = () => {
  try {
    const storage = window.sessionStorage
    return storage
  } catch (error) {
    console.error('Browser window session storage is not accessible')
    return {}
  }
}

export const sessionStoragePayhelm = getSessionStorage()

export const queryObjToStr = (queryObj) => {
  return _.chain(queryObj)
    .toPairs()
    .reject(([k, v]) => _.isNil(v))
    .map(([k, v]) => `${k}=${v}`)
    .join('&')
    .value()
}

export const searchFilterValueLegend = {
  billing_country_code: { title: 'Country Code or Name' },
  billing_province: { title: 'Province' },
  billing_county_name: { title: 'County' },
  brand_ids: { title: 'Brand ID or Name' },
  category_titles: { title: 'Category' },
  currency: { title: 'Currency Code' },
  // customer_id: { title: 'Customer ID', },
  coupon_codes: { title: 'Coupon Code' },
  product_skus: { title: 'Product SKU' },
  skus: { title: 'SKU' },
  utm_campaign: { title: 'UTM Campaign' },
  utm_content: { title: 'UTM Content' },
  utm_medium: { title: 'UTM Medium' },
  utm_source: { title: 'UTM Source' },
  utm_term: { title: 'UTM Term' },
  utm_id: { title: 'UTM ID' },
}

export const searchableFilterValues = Object.keys(searchFilterValueLegend)

export const processSearchFilterInput = (filters, filterKey, input) => {
  if (['billing_country_code', 'brand_ids'].includes(filterKey)) {
    let dictionary = {}
    filters.forEach((filter) => {
      dictionary[filter.title.toLowerCase()] = filter.val
    })
    return dictionary[input.toLowerCase()] || input
  } else return input
}

export const searchFilterInputHandler = (input, filterKey) => {
  switch (filterKey) {
    case 'billing_country_code':
      return input.toUpperCase()
    case 'billing_province' || 'billing_county_name':
      return input
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    case 'currency':
      return input.toUpperCase()
    // case 'customer_id':
    //   return input.toLowerCase()
    default:
      return input
  }
}

export const filterTitleFormatter = (filter, filterKey) => {
  let title = formatNameValueDisplay(filter.title || filter.val)
  let id
  const count = filter.count ? `(${filter.count})` : ''
  if (['billing_country_code', 'brand_ids'].includes(filterKey)) id = `${filter.val}`
  // if (filterKey === 'customer_id') {
  //   const filterStorePrefix = Object.keys(accountEmailDict).map(account => `${accountEmailDict[account].channel}_${account}_`)
  // } // TODO - figure out customer_id filter with multiple possible accounts

  if ((filterKey === 'processing_method' || filterKey === '-processing_method') && title === 'N/A') {
    title = 'No Method'
  }

  const rawVal = filter.val != null ? String(filter.val) : ''
  const tooltip = rawVal && rawVal !== String(title) ? rawVal : undefined

  return (
    <span title={tooltip}>
      {id}
      {id && ' - '}
      {title} {count}
    </span>
  )
}

export {
  getOwnerId,
  isOrganizationWorkspaceUser,
  filterClassicTeamAccountsForIntegrationsOwnerId,
  resolveIntegrationsOwnerId,
} from './integrationsOwnerId'

// [{ id: '', ... }] -> { ids: [], entities: { [id]: entity, ... } }
// Takes optional selectId fn to specify which key should be the id (defaults to id)
export const normalizeEntities = (data, { selectId = ({ id }) => id } = {}) => {
  if (!data) return null

  if (_.isEmpty(data)) {
    return { ids: [], entities: {} }
  }

  return {
    ids: _.map(data, selectId),
    entities: _.reduce(data, (prev, curr) => ({ ...prev, [selectId(curr)]: curr }), {}),
  }
}

export const validateJSONString = (string) => {
  try {
    JSON.parse(string)
    return true
  } catch (e) {
    return false
  }
}

// MUST input an array of promise factories NOT promises - i.e. () => Promise
export const resolvePromiseFactoriesSeq = async (promiseFactories) => {
  const results = []
  for (const promiseFactory of promiseFactories) {
    try {
      const result = await promiseFactory()
      results.push(result)
    } catch (err) {
      console.log('Error in resolvePromisesSeq', err)
      throw new Error(err)
    }
  }

  return results
}

export const convertTo12HourFormat = (militaryTime) => {
  // Parse the military time string
  const [hours, minutes] = militaryTime.split(':')
  let hour = parseInt(hours, 10)

  // Determine AM/PM and adjust hour
  const period = hour >= 12 ? 'PM' : 'AM'
  hour = hour % 12 || 12 // Convert hour 0 to 12

  // Format the time in 12-hour format with AM/PM
  const time12Hour = `${hour}:${minutes} ${period}`;
  return time12Hour;
}

export const acceptedFormatColumns = ['processed_at', 'created_at', 'updated_at', 'fulfilled_at', 'shipping_province', 'last_refund_at', 'payout_dates']

const defaultCharSet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

export const randomString = (
  length,
  charSet = defaultCharSet
) => {
  const charSetSize = charSet.length
  let output = ''
  for (var i = 0; i < length; i++) {
    output += charSet[Math.floor(Math.random() * charSetSize)]
  }
  return output
}

